import { create } from 'zustand';

// Update filter structure for multi-select
interface CommissionFilters {
  affiliateIds?: string[]; // Changed to array
}

/**
 * What a companion card asked this page to open with.
 *
 * It carries beneficiaries, never amounts: the page recomputes what each of
 * them is owed when it loads, so a card published last night can never put a
 * stale figure in front of a brand about to pay it.
 */
export interface CompanionPayoutPreselection {
  actionId: string;
  affiliateIds: string[];
}

interface PendingPayoutState {
  selectedCommissionKeys: string[];
  filters: CommissionFilters;
  /** Set while the page was opened from a card; null the rest of the time. */
  companionPreselection: CompanionPayoutPreselection | null;
  addCommissionKey: (key: string) => void;
  removeCommissionKey: (key: string) => void;
  toggleCommissionSelection: (key: string) => void;
  selectAllCommissions: (keys: string[]) => void;
  clearSelection: () => void;
  // setFilters might need adjustment depending on how multi-select component passes data
  // For now, assume it replaces the array
  setFilters: (newFilters: Partial<CommissionFilters>) => void;
  /** Filters and preselects the beneficiaries a companion card named. */
  preselectFromCompanion: (preselection: CompanionPayoutPreselection) => void;
  clearCompanionPreselection: () => void;
}

export const usePendingPayoutStore = create<PendingPayoutState>((set, _get) => ({
  selectedCommissionKeys: [],
  filters: {
    // Initialize filters as empty arrays or undefined
    affiliateIds: undefined,
  },
  companionPreselection: null,
  addCommissionKey: (key) =>
    set((state) => {
      if (!state.selectedCommissionKeys.includes(key)) {
        return { selectedCommissionKeys: [...state.selectedCommissionKeys, key] };
      }
      return state;
    }),
  removeCommissionKey: (key) =>
    set((state) => ({
      selectedCommissionKeys: state.selectedCommissionKeys.filter((k) => k !== key),
    })),
  toggleCommissionSelection: (key) =>
    set((state) => {
      const { selectedCommissionKeys } = state;
      if (selectedCommissionKeys.includes(key)) {
        return { selectedCommissionKeys: selectedCommissionKeys.filter((k) => k !== key) };
      } else {
        return { selectedCommissionKeys: [...selectedCommissionKeys, key] };
      }
    }),
  selectAllCommissions: (keys) => set({ selectedCommissionKeys: keys }),
  clearSelection: () => set({ selectedCommissionKeys: [] }),
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),
  preselectFromCompanion: (preselection) =>
    set((state) => ({
      filters: { ...state.filters, affiliateIds: [...preselection.affiliateIds] },
      // Ticked rather than paid: the page still asks the brand to confirm, and
      // the table drops any beneficiary its own reading says is not ready.
      selectedCommissionKeys: [...preselection.affiliateIds],
      companionPreselection: preselection,
    })),
  clearCompanionPreselection: () => set({ companionPreselection: null }),
}));
